import { AVAILABLE_MODELS, type Model } from 'js/src/agent/types';
import type { BrandKit, Template } from 'js/src/client';
import { getAIVoiceById } from 'js/src/data/ai-voices-and-avatars/ai-voices/ai-voices-selectors';
import { getBlueprint } from 'js/src/data/blueprints/blueprints-selectors';
import { getBrandKitById } from 'js/src/data/brand-kits/brand-kits-selectors';
import { getTemplateById } from 'js/src/data/templates/templates-selectors';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export const selectSketchControls = (state: RootState) => state.sketchControls;

export const selectSketchControlsTemplate = (state: RootState): ImmutableMap<Template> | null => {
    const controls = state.sketchControls;
    if (!controls?.templateId) return null;

    const template = getTemplateById(state, controls.templateId);
    return template || null;
};

export const selectSketchControlsVoiceName = (state: RootState): string | null => {
    const controls = state.sketchControls;
    if (!controls?.voiceId) return null;

    const voice = getAIVoiceById(state, controls.voiceId);
    return voice?.display_name || null;
};

// The selection is persisted to a year-long cookie, so a model that has since been
// retired can still come back on rehydrate. Dropping unknown values here lets both
// call sites fall through to their default instead of sending a model the backend rejects.
export const selectSketchControlsModel = (state: RootState): Model | null => {
    const model = state.sketchControls?.model ?? null;
    return AVAILABLE_MODELS.some((m) => m.value === model) ? model : null;
};

export const selectSketchControlsModelLabel = (state: RootState): string | null => {
    const controls = state.sketchControls;
    if (!controls?.model) return null;

    const modelOption = AVAILABLE_MODELS.find((m) => m.value === controls.model);
    return modelOption?.label || null;
};

export const selectSketchControlsBlueprintId = (state: RootState): number | null => {
    return state.sketchControls?.blueprintId ?? null;
};

export const selectSketchControlsBlueprintName = (state: RootState): string | null => {
    const controls = state.sketchControls;
    if (!controls?.blueprintId) return null;

    const blueprint = getBlueprint(state, controls.blueprintId);
    return blueprint?.name || null;
};

export const selectSketchControlsBlueprint = (state: RootState) => {
    const controls = state.sketchControls;
    if (!controls?.blueprintId) return null;

    return getBlueprint(state, controls.blueprintId);
};

export const selectSketchControlsBrandKitId = (state: RootState): number | null => {
    return state.sketchControls?.brandKitId ?? null;
};

export const selectSketchControlsBrandKit = (state: RootState): ImmutableMap<BrandKit> | null => {
    const controls = state.sketchControls;
    const brandKitId = controls?.brandKitId;
    if (brandKitId === null || brandKitId === undefined) return null;
    const brandKit = getBrandKitById(state, brandKitId);
    return brandKit || null;
};

export const selectSketchControlsBrandKitName = (state: RootState): string | null => {
    const controls = state.sketchControls;
    const brandKitId = controls?.brandKitId;
    if (brandKitId === null || brandKitId === undefined) return null;
    const brandKit = getBrandKitById(state, brandKitId);
    return brandKit?.get('name') || null;
};
